import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { HealthOverview } from '../api/types';
import { RouterStatusBadge } from '../components/status';
import { Badge, Card, Empty, ErrorAlert, Spinner } from '../components/ui';
import { formatDuration, formatRelative } from '../lib/format';

export function HealthPage() {
  const health = useQuery({
    queryKey: ['health', 'overview'],
    queryFn: () => api.get<HealthOverview>('/health/overview'),
    // Cada consulta sondea los routers en vivo: refrescar más a menudo sería
    // martillearlos sin ganar información.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const data = health.data;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Salud del sistema</h1>
          <p>Estado del software y latencia real contra cada router, medida al cargar esta pantalla.</p>
        </div>
        <button type="button" className="ghost" disabled={health.isFetching} onClick={() => void health.refetch()}>
          {health.isFetching ? 'Midiendo…' : 'Volver a medir'}
        </button>
      </div>

      <ErrorAlert error={health.error} />

      {health.isLoading ? (
        <Spinner label="Midiendo…" />
      ) : data ? (
        <>
          <div className="stat-grid">
            <Card>
              <div className="card-body">
                <div className="stat-label">API</div>
                <div className="stat-value">{formatDuration(data.api.uptimeSeconds)}</div>
                <div className="faint" style={{ fontSize: 12 }}>
                  en marcha · Node {data.api.nodeVersion} · {data.api.memoryRssMb} MB RSS
                </div>
              </div>
            </Card>

            <Card>
              <div className="card-body">
                <div className="stat-label">Base de datos</div>
                <div className="stat-value">
                  {data.db.ok ? `${data.db.latencyMs} ms` : <Badge tone="bad">Caída</Badge>}
                </div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {data.db.ok ? 'PostgreSQL responde' : (data.db.error ?? 'sin detalle')}
                </div>
              </div>
            </Card>

            <Card>
              <div className="card-body">
                <div className="stat-label">Trabajos de fondo</div>
                <div className="stat-value">
                  {data.jobs.enabled ? <Badge tone="ok">Activos</Badge> : <Badge tone="bad">Desactivados</Badge>}
                </div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {data.jobs.lastSessionSnapshotAt
                    ? `último snapshot ${formatRelative(data.jobs.lastSessionSnapshotAt)}`
                    : 'sin snapshots todavía'}
                  {' · '}sondeo {data.jobs.pollRoutersSeconds}s · sesiones {data.jobs.syncSessionsSeconds}s
                </div>
              </div>
            </Card>

            <Card>
              <div className="card-body">
                <div className="stat-label">Pendiente de sincronizar</div>
                <div className="stat-value">{data.sync.hotspotPending + data.sync.pppoePending}</div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {data.sync.hotspotPending} HotSpot · {data.sync.pppoePending} PPPoE
                </div>
              </div>
            </Card>

            <Card>
              <div className="card-body">
                <div className="stat-label">Logins fallidos (24 h)</div>
                <div className="stat-value">
                  {data.security.failedLogins24h > 0 ? (
                    <Badge tone={data.security.failedLogins24h > 20 ? 'bad' : 'warn'}>
                      {data.security.failedLogins24h}
                    </Badge>
                  ) : (
                    0
                  )}
                </div>
                <div className="faint" style={{ fontSize: 12 }}>
                  intentos rechazados en el panel
                </div>
              </div>
            </Card>
          </div>

          <Card>
            {data.routers.length === 0 ? (
              <Empty>No hay routers activos que medir.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Router</th>
                      <th>Estado</th>
                      <th className="num">Latencia ahora</th>
                      <th className="num">CPU</th>
                      <th className="num">Uptime</th>
                      <th>RouterOS</th>
                      <th>Última vez visto</th>
                      <th>Último error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.routers.map((router) => (
                      <tr key={router.id}>
                        <td>
                          <strong>{router.name}</strong>
                        </td>
                        <td>
                          <RouterStatusBadge status={router.status} />
                        </td>
                        <td className="num">
                          {router.probeLatencyMs !== null ? (
                            <Badge tone={router.probeLatencyMs > 1000 ? 'warn' : 'ok'}>
                              {router.probeLatencyMs} ms
                            </Badge>
                          ) : (
                            <Badge tone="bad">sin respuesta</Badge>
                          )}
                        </td>
                        <td className="num">{router.cpuLoad !== null ? `${router.cpuLoad}%` : '—'}</td>
                        <td className="num">
                          {router.uptimeSeconds !== null ? formatDuration(router.uptimeSeconds) : '—'}
                        </td>
                        <td>{router.version ?? '—'}</td>
                        <td className="faint">
                          {router.lastSeenAt ? formatRelative(router.lastSeenAt) : 'nunca'}
                        </td>
                        <td className="faint" style={{ maxWidth: 280 }}>
                          {router.probeError ?? router.lastError ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </main>
  );
}
